import { useState, useRef, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { Sun, Wind, Home, Battery, ChevronDown, Bell, Users, Calendar, DollarSign, Zap, Activity, MessageSquare, Share2, HelpCircle } from 'lucide-react';
import { getParsedCsvData, getDataForDay, getAvailableDays } from '../../data/findhorn_data.js';
import { CustomTooltip } from '../Dashboard'; // path must be relative to where EnergyTabs.js or PvHistoricalTab.js is

// Timeline Dropdown Component
const TimelineDropdown = ({ selectedPeriod, onPeriodChange, label = "Timeline" }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('today');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Generate period options for the past 2 years
    const generatePeriodOptions = () => {
        const options = [];
        const today = new Date();

        // Single day options
        options.push({
            value: 'today',
            label: 'Today',
            startDate: today,
            endDate: today,
            type: 'day'
        });

        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        options.push({
            value: 'yesterday',
            label: 'Yesterday',
            startDate: yesterday,
            endDate: yesterday,
            type: 'day'
        });

        // Week periods
        options.push({ value: 'separator1', label: '--- Weeks ---', disabled: true });

        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        options.push({
            value: 'this-week',
            label: 'This Week',
            startDate: thisWeekStart,
            endDate: today,
            type: 'week'
        });

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
        options.push({
            value: 'last-week',
            label: 'Last Week',
            startDate: lastWeekStart,
            endDate: lastWeekEnd,
            type: 'week'
        });

        // Month periods
        options.push({ value: 'separator2', label: '--- Months ---', disabled: true });

        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        options.push({
            value: 'this-month',
            label: 'This Month',
            startDate: thisMonthStart,
            endDate: today,
            type: 'month'
        });

        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        options.push({
            value: 'last-month',
            label: 'Last Month',
            startDate: lastMonthStart,
            endDate: lastMonthEnd,
            type: 'month'
        });

        // Custom period
        options.push({ value: 'separator3', label: '--- Custom ---', disabled: true });
        options.push({
            value: 'custom',
            label: 'Custom Period',
            type: 'custom'
        });

        // CSV Day options
        options.push({ value: 'separator4', label: '--- CSV Days ---', disabled: true });
        for (let i = 1; i <= 7; i++) {
            options.push({
                value: `csv-day-${i}`,
                label: `Day ${i}`,
                csvDay: i,
                type: 'csv-day'
            });
        }

        return options;
    };

    const periodOptions = generatePeriodOptions();

    const handlePeriodSelect = (option) => {
        if (option.disabled) return;

        setSelectedOption(option.value);

        if (option.value !== 'custom') {
            if (option.type === 'csv-day') {
                onPeriodChange({
                    csvDay: option.csvDay,
                    type: 'csv-day'
                });
            } else {
                onPeriodChange({
                    start: option.startDate,
                    end: option.endDate,
                    type: option.type
                });
            }
            setIsDropdownOpen(false);
        }
    };

    const handleCustomPeriodApply = () => {
        if (customStartDate && customEndDate) {
            const startDate = new Date(customStartDate);
            const endDate = new Date(customEndDate);

            if (startDate <= endDate) {
                onPeriodChange({
                    start: startDate,
                    end: endDate,
                    type: 'custom'
                });
                setSelectedOption('custom');
                setIsDropdownOpen(false);
            }
        }
    };

    const getMaxDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getMinDate = () => {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        return twoYearsAgo.toISOString().split('T')[0];
    };

    const getDisplayLabel = () => {
        if (!selectedPeriod) return 'Select Period';

        const option = periodOptions.find(opt => opt.value === selectedOption);
        if (option && selectedOption !== 'custom') {
            return option.label;
        }

        // Custom or period-based display
        const { start, end } = selectedPeriod;
        if (start && end) {
            const sameDay = start.toDateString() === end.toDateString();
            if (sameDay) {
                return start.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                });
            } else {
                return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            }
        }

        return 'Select Period';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-1 bg-white dark:bg-gray-800 px-3 py-2 rounded text-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
                <Calendar size={14} />
                <span>{getDisplayLabel()}</span>
                <ChevronDown
                    size={16}
                    className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                    <div className="py-1">
                        {periodOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handlePeriodSelect(option)}
                                disabled={option.disabled}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${option.disabled
                                    ? 'text-gray-400 cursor-not-allowed font-medium'
                                    : selectedOption === option.value
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}

                        {/* Custom Period Selector */}
                        {selectedOption === 'custom' && (
                            <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">
                                    Select date range:
                                </label>
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From:</label>
                                        <input
                                            type="date"
                                            min={getMinDate()}
                                            max={getMaxDate()}
                                            value={customStartDate}
                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To:</label>
                                        <input
                                            type="date"
                                            min={customStartDate || getMinDate()}
                                            max={getMaxDate()}
                                            value={customEndDate}
                                            onChange={(e) => setCustomEndDate(e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>
                                    <button
                                        onClick={handleCustomPeriodApply}
                                        disabled={!customStartDate || !customEndDate}
                                        className="w-full mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Apply Period
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Custom tooltip for line chart
const LineChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{`Time: ${label}`}</p>
                {payload.map((entry, index) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {`${entry.name}: ${entry.value.toFixed(3)} kW`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const PvHistoricalTab = ({ view, setView, mockEnergyData, COLORS, data }) => {
    // State for managing selected periods for each chart
    const [dailyProfilePeriod, setDailyProfilePeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });
    const [householdSourcesPeriod, setHouseholdSourcesPeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });
    const [communitySourcesPeriod, setCommunitySourcesPeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });

    // Fixed Chart Component with proper CSV parameter handling

    // State for CSV data
    const [csvData, setCsvData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [csvError, setCsvError] = useState(null);

    // Load CSV data on component mount
    useEffect(() => {
        loadCSVData();
    }, []);

    // Update chart data when selected period changes
    useEffect(() => {
        if (csvData.length > 0) {
            updateChartData();
        }
    }, [dailyProfilePeriod, csvData]);

    // FIXED: Improved loadCSVData function with correct parameters
    const loadCSVData = async () => {
        try {
            setLoading(true);
            setCsvError(null);

            console.log("🔄 Loading CSV data from local module...");

            // FIX: Use DATA instead of FORECAST
            const csvResult = await getParsedCsvData('DATA');

            console.log("📊 CSV Module Results:");
            console.log("- Total rows:", csvResult.totalRows);
            console.log("- Headers:", csvResult.headers);
            console.log("- Sample data:", csvResult.data.slice(0, 3));

            if (csvResult.totalRows === 0) {
                throw new Error("No valid data found in CSV module");
            }

            // FIX: Pass correct parameters to helper functions
            console.log("🔍 Detailed CSV Content Analysis:");
            console.log("- First row data:", JSON.stringify(csvResult.data[0], null, 2));

            // FIX: Use DATA type for this chart
            const availableDays = await getAvailableDays('DATA', csvResult.data);
            console.log("- Available days:", availableDays);

            // FIX: Test getting data for day 1 with DATA type
            const day1Data = await getDataForDay(1, 'DATA', csvResult.data);
            console.log("📈 Day 1 sample data:", day1Data.slice(0, 5));

            setCsvData(csvResult.data);
            setLoading(false);

            console.log("✅ CSV data loaded successfully from module!");

        } catch (err) {
            console.error('❌ Error loading CSV from module:', err);
            setCsvError(`Failed to load CSV data: ${err.message}`);
            setLoading(false);

            // Generate fallback data
            console.log("🔄 Generating fallback data...");
            generateFallbackChartData();
        }
    };

    // FIXED: Improved updateChartData function with correct function calls
    const updateChartData = async () => {
        if (csvData.length === 0) {
            console.log("❌ No CSV data available for chart update");
            return;
        }

        try {
            console.log("🔄 Updating chart data for period:", dailyProfilePeriod);

            const { start, end, type, csvDay } = dailyProfilePeriod;

            // FIX: Use DATA type for this chart
            const availableDays = await getAvailableDays('DATA', csvData);

            console.log("📅 Available days:", availableDays);

            if (type === 'csv-day' && csvDay) {
                // Direct CSV day selection
                console.log("📊 Direct CSV day selection:", csvDay);
                
                const transformedData = await getDataForDay(csvDay, 'DATA', csvData);

                console.log("📈 Transformed data for chart:", {
                    totalPoints: transformedData.length,
                    sampleData: transformedData.slice(0, 3),
                    timeRange: transformedData.length > 0 ? {
                        start: transformedData[0].time,
                        end: transformedData[transformedData.length - 1].time
                    } : null
                });

                if (transformedData.length === 0) {
                    console.warn("⚠️ No data found for selected day, using fallback");
                    generateFallbackChartData();
                    return;
                }

                setChartData(transformedData);
            } else if (type === 'day') {
                // FIXED: Map any calendar date to available CSV days (1-7)
                // Use day of week to select which CSV day to use
                const dayOfWeek = start.getDay(); // 0 = Sunday, 6 = Saturday
                
                // Map day of week to CSV days (1-7)
                // If CSV has 7 days, treat them as Mon-Sun
                const csvDayIndex = dayOfWeek % availableDays.length;
                const selectedDay = availableDays[csvDayIndex];

                console.log("📊 Calendar date:", start.toDateString());
                console.log("📊 Day of week:", dayOfWeek);
                console.log("📊 Selected CSV day:", selectedDay);

                // FIX: Get data for the selected day using DATA type
                const transformedData = await getDataForDay(selectedDay, 'DATA', csvData);

                console.log("📈 Transformed data for chart:", {
                    totalPoints: transformedData.length,
                    sampleData: transformedData.slice(0, 3),
                    timeRange: transformedData.length > 0 ? {
                        start: transformedData[0].time,
                        end: transformedData[transformedData.length - 1].time
                    } : null
                });

                if (transformedData.length === 0) {
                    console.warn("⚠️ No data found for selected day, using fallback");
                    generateFallbackChartData();
                    return;
                }

                setChartData(transformedData);

            } else {
                // Multi-day aggregation for weeks/months
                const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                const aggregatedData = [];

                for (let i = 0; i < daysDiff; i++) {
                    const currentDate = new Date(start);
                    currentDate.setDate(start.getDate() + i);
                    
                    // Map each day to a CSV day based on day of week
                    const dayOfWeek = currentDate.getDay();
                    const csvDayIndex = dayOfWeek % availableDays.length;
                    const selectedDay = availableDays[csvDayIndex];

                    // FIX: Use DATA type for getDataForDay
                    const dayData = await getDataForDay(selectedDay, 'DATA', csvData);

                    const totalGeneration = dayData.reduce((sum, row) => sum + row.generation, 0);
                    const totalDemand = dayData.reduce((sum, row) => sum + row.demand, 0);

                    aggregatedData.push({
                        time: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        generation: totalGeneration,
                        demand: totalDemand,
                        production: totalGeneration,
                        consumption: totalDemand
                    });
                }

                console.log("📊 Multi-day aggregated data:", aggregatedData.length);
                setChartData(aggregatedData);
            }
        } catch (error) {
            console.error("❌ Error updating chart data:", error);
            setCsvError(`Error updating chart: ${error.message}`);
            generateFallbackChartData();
        }
    };

    // Enhanced fallback data generation
    const generateFallbackChartData = () => {
        console.log("Generating fallback chart data...");

        const fallbackData = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let mins = 0; mins < 60; mins += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

                // Enhanced solar generation curve
                let generation = 0;
                if (hour >= 6 && hour <= 18) {
                    const midday = 12;
                    const distance = Math.abs(hour + mins / 60 - midday);
                    generation = Math.max(0, 3 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.3);
                }

                // More realistic demand curve
                let demand = 0.8; // Base load
                if (hour >= 7 && hour <= 9) demand += 1.5; // Morning peak
                if (hour >= 17 && hour <= 21) demand += 2.0; // Evening peak
                demand += Math.random() * 0.4; // Some variation

                fallbackData.push({
                    time: timeString,
                    timeDecimal: hour + mins / 60,
                    generation: parseFloat(generation.toFixed(3)),
                    demand: parseFloat(demand.toFixed(3)),
                    production: parseFloat(generation.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        }

        console.log("Generated fallback data points:", fallbackData.length);
        setChartData(fallbackData);
    };

    // Additional helper function to check if functions are imported correctly
    const debugCSVFunctions = () => {
        console.log("🔍 Debugging CSV function availability:");
        console.log("- getParsedCsvData:", typeof getParsedCsvData);
        console.log("- getAvailableDays:", typeof getAvailableDays);
        console.log("- getDataForDay:", typeof getDataForDay);

        // Test with sample calls
        try {
            getParsedCsvData('DATA').then(result => {
                console.log("✅ getParsedCsvData test successful:", result.totalRows, "rows");
            }).catch(err => {
                console.error("❌ getParsedCsvData test failed:", err.message);
            });
        } catch (err) {
            console.error("❌ getParsedCsvData function error:", err.message);
        }
    };

    // Call this in useEffect for debugging if needed
    useEffect(() => {
        debugCSVFunctions();
    }, []);

    // Mock data for peak demand
    const peakDemandData = [
        { day: 'Mon', value: 2.17, time: '15h45' },
        { day: 'Tues', value: 3.80, time: '10h45' },
        { day: 'Wed', value: 1.99, time: '09h30' },
        { day: 'Thur', value: 2.23, time: '10h45' },
        { day: 'Fri', value: 4.50, time: '07h45' },
        { day: 'Sat', value: 1.91, time: '13h30' },
        { day: 'Sun', value: 2.48, time: '16h15' }
    ];

    // Custom tooltip for peak demand
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-2 border border-gray-200 rounded shadow-md">
                    <p className="text-sm">Time of day when peak demand occurred: <strong>{payload[0].payload.time}</strong></p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className='w-full'>
            {/* Container for the view buttons - positioned at top right */}
            <div className="flex justify-end mb-4">
                <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">View:</span>
                    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'household' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('household')}
                        >
                            Household
                        </button>
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'community' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('community')}
                        >
                            Community
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Daily Profile (Household) - Now with real data */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Daily Profile (Household)</h3>
                        <TimelineDropdown
                            selectedPeriod={dailyProfilePeriod}
                            onPeriodChange={setDailyProfilePeriod}
                        />
                    </div>

                    {loading ? (
                        <div className="h-72 flex items-center justify-center">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">Loading data...</p>
                            </div>
                        </div>
                    ) : csvError ? (
                        <div className="h-72 flex items-center justify-center">
                            <div className="text-center">
                                <p className="text-red-600 dark:text-red-400 text-sm mb-1">{csvError}</p>
                                <p className="text-gray-600 dark:text-gray-400 text-xs">Using sample data</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="time"
                                        tick={{ fontSize: 12 }}
                                        interval="preserveStartEnd"
                                        tickFormatter={(value, index) => {
                                            // For multi-day data (less than 40 points), show more ticks
                                            if (chartData.length <= 40) {
                                                // Show every 2-3 ticks for weeks/months
                                                const interval = chartData.length <= 10 ? 1 : Math.ceil(chartData.length / 8);
                                                if (index % interval === 0) return value;
                                            } else {
                                                // For single day with many points, show fewer ticks
                                                if (index % 16 === 0) return value;
                                            }
                                            return '';
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12 }}
                                        label={{ value: 'Power (kW)', angle: -90, position: 'insideLeft' }}
                                    />
                                    <Tooltip content={<LineChartTooltip />} />
                                    <Line
                                        type="monotone"
                                        dataKey="generation"
                                        stroke="#00C49F"
                                        strokeWidth={3}
                                        name="Generation"
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="demand"
                                        stroke="#FF8042"
                                        strokeWidth={3}
                                        name="Demand"
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>

                            {/* Legend */}
                            <div className="flex justify-center space-x-6 mt-2">
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Generation</span>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">Demand</span>
                                </div>
                            </div>

                            {/* Data Info */}
                            <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {dailyProfilePeriod.type === 'day' ? (
                                    <>
                                        Data for {dailyProfilePeriod.start.toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })} • {chartData.length} data points (15-min intervals)
                                    </>
                                ) : (
                                    <>
                                        Period: {dailyProfilePeriod.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {dailyProfilePeriod.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • {chartData.length} days (daily totals)
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Energy Sources (Household) */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className='flex justify-between items-center mb-4'>
                        <h3 className="font-medium mb-4 text-gray-900 dark:text-gray-100">Energy Sources (Household)</h3>
                        <TimelineDropdown
                            selectedPeriod={householdSourcesPeriod}
                            onPeriodChange={setHouseholdSourcesPeriod}
                        />
                    </div>

                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={mockEnergyData.historicalHouseHoldSources}
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {mockEnergyData.historicalHouseHoldSources.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 flex justify-center">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center">
                                <Sun size={16} className="text-yellow-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Community</span>
                            </div>
                            <div className="flex items-center">
                                <Wind size={16} className="text-blue-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Household</span>
                            </div>
                            <div className="flex items-center">
                                <Home size={16} className="text-green-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Utility</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Daily Peak Demand */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">Daily Peak Demand</h3>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data} margin={{ top: 30, right: 30, left: 20, bottom: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="day" />
                                    <YAxis
                                        label={{ value: 'Peak load of your dwelling (kW)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                                        domain={[0, 5]}
                                        ticks={[0, 1, 2, 3, 4, 5]}
                                    />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }} />
                                    <Bar
                                        dataKey="value"
                                        fill="#FFECD1"
                                        stroke="#FFC107"
                                        strokeWidth={1}
                                        isAnimationActive={false}
                                    >
                                        {data.map((entry, index) => (
                                            <text
                                                x={0}
                                                y={0}
                                                dx={30}
                                                dy={-10}
                                                textAnchor="middle"
                                                fill="#1A202C"
                                                fontSize={12}
                                                key={`time-${index}`}
                                            >
                                                {entry.time}
                                            </text>
                                        ))}
                                        <LabelList dataKey="value" position="center" formatter={(value) => `${value.toFixed(1)}kW`} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-4 text-center">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Day of the week</p>
                        </div>
                    </div>
                </div>

                {/* Energy Sources (Community) */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Energy Sources (Community)</h3>
                        <TimelineDropdown
                            selectedPeriod={communitySourcesPeriod}
                            onPeriodChange={setCommunitySourcesPeriod}
                        />
                    </div>

                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie
                                data={mockEnergyData.historicalCommunitySources}
                                cx="50%"
                                cy="50%"
                                outerRadius={70}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                                {mockEnergyData.historicalCommunitySources.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 flex justify-center">
                        <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center">
                                <Sun size={16} className="text-yellow-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Solar</span>
                            </div>
                            <div className="flex items-center">
                                <Wind size={16} className="text-blue-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Wind</span>
                            </div>
                            <div className="flex items-center">
                                <Home size={16} className="text-green-500 mr-1" />
                                <span className="text-xs text-gray-700 dark:text-gray-300">Utility </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* How much of your PV generation do you use? */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className='flex mb-2'>
                        <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">How much of your PV generation do you use?</h3>
                    </div>
                    <div className='flex items-center justify-center w-full'>
                        <div className="w-24 h-24 rounded-full border-4 border-blue-500 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">23%</span>
                        </div>
                    </div>
                </div>

                {/* How much of your demand is met by your PV? */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <div className='flex mb-2'>
                        <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">How much of your demand is met by your PV?</h3>
                    </div>
                    <div className="flex items-center justify-center w-full">
                        <div className="w-24 h-24 rounded-full border-4 border-blue-500 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">59%</span>
                        </div>
                    </div>
                </div>

                {/* How much of your demand is met by community RE? */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow md:col-span-2">
                    <div className='flex mb-2'>
                        <h3 className="font-medium mb-2 text-gray-900 dark:text-gray-100">How much of your demand is met by community RE (including your PV)</h3>
                    </div>
                    <div className="flex items-center justify-center w-full">
                        <div className="w-24 h-24 rounded-full border-4 border-blue-500 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">39%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PvHistoricalTab;